import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { C } from './constants';
import { getPeriodeActuelle, getPeriodeDebut } from './App';

// ─────────────────────────────────────────────────────────────────────────────
// 📈 MES RÉSULTATS — ce que je fais, ce que ça donne, et le lien entre les deux.
// Tout est calculé à partir de ce que la personne a déjà dans l'app (prospects, clientes,
// recrues, assiduité, suivi CA) : rien à ressaisir, rien n'est envoyé à d'autres personnes.
// ─────────────────────────────────────────────────────────────────────────────

const JOUR = 86400000;
const lireJson = (x, defaut) => {
  try { const v = typeof x === 'string' ? JSON.parse(x) : x; return v == null ? defaut : v; } catch { return defaut; }
};
const nombre = (v) => { const n = parseFloat(String(v == null ? '' : v).replace(',', '.')); return isNaN(n) ? 0 : n; };
const euros = (n) => (Math.round(nombre(n) * 100) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 }) + ' €';

// Date (en ms) d'une valeur AAAA-MM-JJ, JJ/MM/AAAA ou horodatage ; null si illisible
export const versMs = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v > 1e11 ? v : null;
  const s = String(v).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], 12).getTime();
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], 12).getTime();
  const t = Date.parse(s);
  return isNaN(t) ? null : t;
};

// Groupe d'un prospect d'après son statut
export const groupeStatut = (statut) => {
  const s = String(statut || '').toLowerCase();
  if (s.includes('converti')) return 'converti';
  if (s.includes('pas int')) return 'non';
  if (s.includes('invit') || s.includes('flexion')) return 'presentation';
  if (s.includes('contact') || s.includes('chaud') || s.includes('tiède') || s.includes('tiede') || s.includes('froid')) return 'contact';
  return 'nouveau';
};

// Calcul pur (testable) : tout ce que la page affiche
export function calculerResultats({ prospects, clients, distributeurs, assiduite, suiviCA, objPerso, maintenant, numPeriode, debutPeriode }) {
  const now = maintenant || Date.now();
  const debut = debutPeriode - 12 * 3600000; // début de journée du 1er jour de la période
  const dansPeriode = (v) => { const t = versMs(v); return t != null && t >= debut && t <= now + JOUR; };
  const joursEcoules = Math.min(21, Math.max(1, Math.floor((now - debut) / JOUR) + 1));
  const aujourdhui = new Date(now); aujourdhui.setHours(12, 0, 0, 0);
  const auj = aujourdhui.getTime();

  const pros = Array.isArray(prospects) ? prospects : [];
  const cls = Array.isArray(clients) ? clients : [];
  const dis = Array.isArray(distributeurs) ? distributeurs : [];

  const dateProspect = (p) => (versMs(p.date) != null ? p.date : p.id);
  const jAct = (assiduite && assiduite['p' + numPeriode]) || {};
  const joursActifs = (Array.isArray(jAct.jours) ? jAct.jours : Object.values(jAct.jours || {})).filter(Boolean).length;

  const nouveauxProspects = pros.filter((p) => dansPeriode(dateProspect(p))).length;
  const relancesFaites = pros.reduce((s, p) => s + (Array.isArray(p.journal) ? p.journal.filter((j) => /relance effectu/i.test(String(j && j.msg)) && dansPeriode(j.date)).length : 0), 0);
  const relancesEnRetard = pros.filter((p) => { const t = versMs(p.relance); return t != null && t < auj && groupeStatut(p.statut) !== 'converti' && groupeStatut(p.statut) !== 'non'; }).length;
  const chaudsSansRelance = pros.filter((p) => /chaud/i.test(String(p.statut)) && !String(p.relance || '').trim()).length;
  const nouvellesClientes = cls.filter((c) => dansPeriode(c.dateAjout)).length;
  const commandesPeriode = cls.reduce((s, c) => s + (Array.isArray(c.commandes) ? c.commandes.filter((m) => dansPeriode(m.date)).length : 0), 0);

  const groupes = { nouveau: 0, contact: 0, presentation: 0, converti: 0, non: 0 };
  pros.forEach((p) => { groupes[groupeStatut(p.statut)]++; });
  const convertiesPeriode = pros.filter((p) => p.convertiVers && dansPeriode(p.dateConversion));
  const recruesProspects = convertiesPeriode.filter((p) => String(p.convertiVers).startsWith('distrib')).length;
  const clientesProspects = convertiesPeriode.filter((p) => p.convertiVers === 'client').length;
  const recruesEnreg = dis.filter((d) => dansPeriode(d.dateEnreg)).length;
  const sCA = (suiviCA && suiviCA['p' + numPeriode]) || {};
  const recruesPeriode = Math.max(nombre(sCA.recrues), recruesProspects, recruesEnreg, nombre(objPerso && objPerso.recruesReal));
  const caPeriode = nombre(sCA.ca) || nombre(objPerso && objPerso.ca);
  const caPerso = nombre(sCA.caPerso) || nombre(objPerso && objPerso.caPerso);
  const objectifCA = nombre(sCA.obj) || nombre(objPerso && objPerso.caObj);
  const objectifRecrues = Math.max(0, parseInt(objPerso && objPerso.recruesObj, 10) || 0);

  // Les 6 dernières périodes (la période en cours incluse)
  const lignes = [];
  for (let n = numPeriode - 5; n <= numPeriode; n++) {
    if (n < 1) continue;
    const a = (assiduite && assiduite['p' + n]) || {};
    const j = (Array.isArray(a.jours) ? a.jours : Object.values(a.jours || {})).filter(Boolean).length;
    const s = (suiviCA && suiviCA['p' + n]) || {};
    let debutN = null; try { debutN = getPeriodeDebut(n); } catch { debutN = null; }
    lignes.push({ num: n, courante: n === numPeriode, debut: debutN, jours: j, ca: nombre(s.ca), caPerso: nombre(s.caPerso), recrues: nombre(s.recrues) });
  }
  // Lien entre ce qu'elle fait et ce que ça donne (périodes terminées seulement)
  const finies = lignes.filter((l) => !l.courante && (l.jours > 0 || l.ca > 0));
  let lien = null;
  if (finies.length >= 3) {
    const tri = finies.slice().sort((a, b) => b.jours - a.jours);
    const moitie = Math.ceil(tri.length / 2);
    const haut = tri.slice(0, moitie), bas = tri.slice(moitie);
    const moy = (arr) => arr.reduce((s, l) => s + l.ca, 0) / Math.max(1, arr.length);
    const mH = moy(haut), mB = moy(bas);
    if (bas.length && mH > mB * 1.15 && mH > 0) lien = { type: 'positif', mH, mB, seuil: Math.min(...haut.map((l) => l.jours)) };
    else lien = { type: 'neutre' };
  }
  return { joursEcoules, joursActifs, nouveauxProspects, relancesFaites, relancesEnRetard, chaudsSansRelance, nouvellesClientes, commandesPeriode, groupes, totalProspects: pros.length, recruesPeriode, clientesProspects, caPeriode, caPerso, objectifCA, objectifRecrues, lignes, lien };
}

// ── Présentation ─────────────────────────────────────────────────────────────
const Carte = ({ titre, children }) => (
  <div style={{ background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: 14, padding: '1rem', marginBottom: '1rem' }}>
    <div style={{ fontSize: '.62rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: C.rose, marginBottom: '.7rem' }}>{titre}</div>
    {children}
  </div>
);
const Chiffre = ({ valeur, libelle, sous, couleur }) => (
  <div style={{ background: C.creme, border: `1px solid ${C.pale}`, borderRadius: 12, padding: '.65rem .5rem', textAlign: 'center' }}>
    <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.5rem', fontWeight: 600, color: couleur || C.brun, lineHeight: 1.1 }}>{valeur}</div>
    <div style={{ fontSize: '.66rem', color: C.texte, marginTop: '.2rem', lineHeight: 1.3 }}>{libelle}</div>
    {sous && <div style={{ fontSize: '.58rem', color: C.gris, marginTop: '.1rem' }}>{sous}</div>}
  </div>
);
const Barre = ({ pct, couleur }) => (
  <div style={{ height: 7, background: C.pale, borderRadius: 10, overflow: 'hidden' }}>
    <div style={{ height: '100%', width: Math.max(0, Math.min(100, pct)) + '%', background: couleur || C.rose, borderRadius: 10, transition: 'width .4s' }} />
  </div>
);
const grille = { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '.55rem' };

export function MesResultats({ uid, prospects, clients, distributeurs, objPerso, actionsJour, actionsTotal }) {
  const [chargement, setChargement] = useState(true);
  const [donnees, setDonnees] = useState({ assiduite: {}, suiviCA: {} });

  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        const d = snap.exists() ? snap.data() : {};
        if (vivant) setDonnees({ assiduite: lireJson(d['db-assiduite'], {}), suiviCA: lireJson(d['db-suivi-ca'], {}) });
      } catch { /* hors ligne : on affiche ce qu'on a */ }
      if (vivant) setChargement(false);
    })();
    return () => { vivant = false; };
  }, [uid]);

  const num = getPeriodeActuelle();
  let debut; try { debut = getPeriodeDebut(num).getTime(); } catch { debut = Date.now() - 10 * JOUR; }
  const r = calculerResultats({ prospects, clients, distributeurs, assiduite: donnees.assiduite, suiviCA: donnees.suiviCA, objPerso, numPeriode: num, debutPeriode: debut });

  const pctJours = Math.round((r.joursActifs / r.joursEcoules) * 100);
  const objectifRecrues = r.objectifRecrues;
  const vide = r.totalProspects === 0 && r.joursActifs === 0 && r.caPeriode === 0 && r.recruesPeriode === 0;

  // Ce qui peut faire la différence (3 conseils maximum, les plus utiles d'abord)
  const conseils = [];
  if (r.relancesEnRetard > 0) conseils.push(`${r.relancesEnRetard} relance${r.relancesEnRetard > 1 ? 's' : ''} en retard : c'est là que se cachent tes prochaines ventes. Fais-en une aujourd'hui.`);
  if (r.chaudsSansRelance > 0) conseils.push(`${r.chaudsSansRelance} prospect${r.chaudsSansRelance > 1 ? 's' : ''} « chaud${r.chaudsSansRelance > 1 ? 's' : ''} » sans relance prévue. Une date dans l'agenda, et ça avance.`);
  if (pctJours < 50 && r.joursEcoules >= 4) conseils.push(`Tu as été active ${r.joursActifs} jour${r.joursActifs > 1 ? 's' : ''} sur ${r.joursEcoules}. Une seule action par jour suffit pour garder le rythme.`);
  if (r.nouveauxProspects === 0 && r.joursEcoules >= 3) conseils.push('Aucun nouveau prospect depuis le début de la période. Vise 3 nouveaux contacts cette semaine : ils font les résultats du mois prochain.');
  const aFaire = conseils.slice(0, 3);

  const etapes = [
    ['Nouveaux', r.groupes.nouveau, C.gris],
    ['En contact', r.groupes.contact, C.lilas],
    ['Présentation / réflexion', r.groupes.presentation, C.or],
    ['Converties', r.groupes.converti, C.vert],
  ];
  const maxEtape = Math.max(1, ...etapes.map((e) => e[1]));

  return (
    <div>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.35rem', fontWeight: 300, color: C.brun, marginBottom: '.2rem' }}>Mes <em style={{ fontStyle: 'italic', color: C.rose }}>résultats</em></div>
      <div style={{ fontSize: '.74rem', color: C.gris, marginBottom: '1rem', lineHeight: 1.6 }}>Ce que tu fais, ce que ça donne, et le lien entre les deux. Calculé tout seul à partir de ton app : rien à ressaisir. Période en cours : jour {r.joursEcoules} sur 21.</div>

      {vide && !chargement && (
        <div style={{ background: '#FFF6E0', borderRadius: 12, padding: '.8rem 1rem', marginBottom: '1rem', fontSize: '.78rem', color: '#8A5A00', lineHeight: 1.6 }}>
          Rien à afficher pour l'instant. Valide tes actions du jour, ajoute tes premiers prospects et renseigne ton CA : cette page se remplit toute seule.
        </div>
      )}

      <Carte titre="🌱 Ce que tu fais · cette période">
        <div style={{ marginBottom: '.8rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', color: C.texte, marginBottom: '.3rem' }}>
            <span>Jours où tu as été active</span><strong style={{ color: pctJours >= 70 ? C.vert : C.brun }}>{r.joursActifs} / {r.joursEcoules}</strong>
          </div>
          <Barre pct={pctJours} couleur={pctJours >= 70 ? C.vert : C.rose} />
        </div>
        <div style={grille}>
          <Chiffre valeur={`${actionsJour || 0}/${actionsTotal || 5}`} libelle="actions faites aujourd'hui" />
          <Chiffre valeur={r.nouveauxProspects} libelle="nouveaux prospects" sous="ajoutés cette période" />
          <Chiffre valeur={r.relancesFaites} libelle="relances faites" sous={r.relancesEnRetard > 0 ? `${r.relancesEnRetard} en retard` : 'aucune en retard'} couleur={r.relancesEnRetard > 0 ? '#C44B1A' : C.brun} />
          <Chiffre valeur={r.nouvellesClientes} libelle="nouvelles clientes" sous={`${r.commandesPeriode} commande${r.commandesPeriode > 1 ? 's' : ''} saisie${r.commandesPeriode > 1 ? 's' : ''}`} />
        </div>
      </Carte>

      <Carte titre="🎯 Ce que ça donne · ton parcours prospects">
        {r.totalProspects === 0
          ? <div style={{ fontSize: '.76rem', color: C.gris }}>Ajoute des prospects dans l'onglet Prospects : tu verras ici combien avancent, étape par étape.</div>
          : (
            <>
              {etapes.map(([lib, n, col]) => (
                <div key={lib} style={{ marginBottom: '.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', color: C.texte, marginBottom: '.2rem' }}><span>{lib}</span><strong>{n}</strong></div>
                  <Barre pct={(n / maxEtape) * 100} couleur={col} />
                </div>
              ))}
              <div style={{ fontSize: '.66rem', color: C.gris, marginTop: '.4rem' }}>
                {r.totalProspects} prospect{r.totalProspects > 1 ? 's' : ''} au total{r.groupes.non ? ` · ${r.groupes.non} pas intéressé${r.groupes.non > 1 ? 's' : ''} (normal : ça fait partie du jeu)` : ''}.
              </div>
            </>
          )}
        <div style={{ ...grille, marginTop: '.8rem' }}>
          <Chiffre valeur={r.caPeriode > 0 ? euros(r.caPeriode) : '—'} libelle="CA de la période" sous={r.objectifCA > 0 ? `objectif ${euros(r.objectifCA)}` : 'objectif à renseigner'} couleur={C.vert} />
          <Chiffre valeur={r.recruesPeriode} libelle={r.recruesPeriode > 1 ? 'recrues' : 'recrue'} sous="cette période" couleur={C.brun} />
        </div>
        {r.objectifCA > 0 && r.caPeriode > 0 && (
          <div style={{ marginTop: '.7rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.68rem', color: C.gris, marginBottom: '.25rem' }}><span>Progression vers ton objectif CA</span><strong>{Math.min(999, Math.round((r.caPeriode / r.objectifCA) * 100))} %</strong></div>
            <Barre pct={(r.caPeriode / r.objectifCA) * 100} couleur={C.vert} />
          </div>
        )}
        {objectifRecrues > 0 && (
          <div style={{ marginTop: '.8rem', fontSize: '.72rem', color: C.texte }}>
            🎯 Objectif recrues : <strong style={{ color: r.recruesPeriode >= objectifRecrues ? C.vert : C.rose }}>{r.recruesPeriode} / {objectifRecrues}{r.recruesPeriode >= objectifRecrues ? ' 🎉' : ''}</strong>
          </div>
        )}
      </Carte>

      <Carte titre="🔗 Le lien entre les deux">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.7rem', color: C.texte }}>
            <thead>
              <tr style={{ color: C.gris, fontSize: '.6rem', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                <th style={{ textAlign: 'left', padding: '.25rem .3rem' }}>Période</th>
                <th style={{ textAlign: 'right', padding: '.25rem .3rem' }}>Jours actifs</th>
                <th style={{ textAlign: 'right', padding: '.25rem .3rem' }}>CA</th>
                <th style={{ textAlign: 'right', padding: '.25rem .3rem' }}>Recrues</th>
              </tr>
            </thead>
            <tbody>
              {r.lignes.map((l) => (
                <tr key={l.num} style={{ borderTop: `1px solid ${C.pale}`, background: l.courante ? C.rose + '12' : 'transparent', fontWeight: l.courante ? 700 : 400 }}>
                  <td style={{ padding: '.35rem .3rem' }}>{l.debut ? l.debut.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : 'P' + l.num}{l.courante ? ' · en cours' : ''}</td>
                  <td style={{ padding: '.35rem .3rem', textAlign: 'right' }}>{l.jours || '—'}</td>
                  <td style={{ padding: '.35rem .3rem', textAlign: 'right' }}>{l.ca > 0 ? euros(l.ca) : '—'}</td>
                  <td style={{ padding: '.35rem .3rem', textAlign: 'right' }}>{l.recrues || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: '.76rem', color: C.texte, lineHeight: 1.65, marginTop: '.7rem', background: C.creme, borderRadius: 10, padding: '.65rem .8rem' }}>
          {r.lien && r.lien.type === 'positif' && <>Les périodes où tu as été active au moins <strong>{r.lien.seuil} jours</strong>, ton CA moyen est de <strong>{euros(r.lien.mH)}</strong>, contre <strong>{euros(r.lien.mB)}</strong> les autres. Ce que tu fais pèse vraiment.</>}
          {r.lien && r.lien.type === 'neutre' && <>Pas encore de lien net entre tes jours actifs et ton CA sur tes dernières périodes. Continue de tout noter : les tendances se précisent avec le temps.</>}
          {!r.lien && <>Reviens après 3 périodes complètes : tu verras ici si tes jours d'action se retrouvent dans ton CA. (Il faut que ton CA soit noté dans « Suivi CA ».)</>}
        </div>
      </Carte>

      <Carte titre="✨ Ce qui peut faire la différence">
        {aFaire.length === 0
          ? <div style={{ fontSize: '.78rem', color: C.vert, fontWeight: 700 }}>Beau rythme. Garde le cap : régularité d'abord, résultats ensuite.</div>
          : aFaire.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: '.5rem', fontSize: '.76rem', color: C.texte, lineHeight: 1.6, marginBottom: '.45rem' }}>
              <span style={{ color: C.rose, fontWeight: 700 }}>{i + 1}.</span><span>{t}</span>
            </div>
          ))}
      </Carte>
    </div>
  );
}
